import { useState } from 'react';
import { useThemeStore } from '../../stores/theme.store';
import {
  useFinancialOverview,
  useFinancialTransactions,
  useFinancialAccounts,
  type TransactionFilters,
  type AccountFilters,
} from '../../hooks/useFinancialOverview';
import {
  BarChart3,
  Wallet,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Users,
  Search,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Shield,
  ArrowUpDown,
  CreditCard,
} from 'lucide-react';

function fmt(value: string | number | null | undefined): string {
  if (value === null || value === undefined) return 'N/A';
  const num = typeof value === 'string' ? parseInt(value, 10) : value;
  if (isNaN(num)) return 'N/A';
  return `R$ ${(num / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function getDefaultDates() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  return {
    startDate: start.toISOString().split('T')[0],
    endDate: now.toISOString().split('T')[0],
  };
}

export function FinancialOverviewPage() {
  const { isDarkMode } = useThemeStore();
  const defaults = getDefaultDates();
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [txFilters, setTxFilters] = useState<TransactionFilters>({ page: 1, limit: 10 });
  const [accFilters, setAccFilters] = useState<AccountFilters>({ page: 1, limit: 10, sortBy: 'totalReceived', sortOrder: 'desc' });
  const [accSearch, setAccSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'transactions' | 'accounts'>('transactions');

  const { data: overview, isLoading: loadingOverview } = useFinancialOverview({ startDate, endDate });
  const { data: txData, isLoading: loadingTx } = useFinancialTransactions({ ...txFilters, startDate, endDate });
  const { data: accData, isLoading: loadingAcc } = useFinancialAccounts({ ...accFilters, search: accSearch });

  const bg = isDarkMode ? 'bg-gray-900' : 'bg-gray-50';
  const cardBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const border = isDarkMode ? 'border-gray-700' : 'border-gray-200';
  const inputBg = isDarkMode ? 'bg-gray-700 text-white border-gray-600' : 'bg-white text-gray-900 border-gray-300';

  const netPositionValue = overview?.netPosition ? parseInt(overview.netPosition, 10) : null;
  const netPositionColor = netPositionValue === null ? textSecondary : netPositionValue >= 0 ? 'text-green-500' : 'text-red-500';

  return (
    <div className={`min-h-screen ${bg} p-6`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-blue-500" />
            <div>
              <h1 className={`text-2xl font-bold ${textPrimary}`}>Visão Financeira</h1>
              <p className={textSecondary}>Painel consolidado de receitas, taxas e contas</p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${inputBg}`} />
            <span className={textSecondary}>até</span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
              className={`px-3 py-2 rounded-lg border text-sm ${inputBg}`} />
          </div>
        </div>

        {/* KPI Cards */}
        {loadingOverview ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={`${cardBg} rounded-xl p-4 animate-pulse`}>
                <div className="h-4 bg-gray-300 rounded w-2/3 mb-2" />
                <div className="h-6 bg-gray-300 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : overview ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
            <KpiCard icon={<Wallet className="w-5 h-5" />} label="Saldo Escrow" value={overview.escrowError ? 'Indisponível' : fmt(overview.escrowBalance)} color="blue" isDarkMode={isDarkMode} error={overview.escrowError} />
            <KpiCard icon={<Users className="w-5 h-5" />} label="Saldo Virtual Total" value={fmt(overview.totalVirtualBalance)} color="purple" isDarkMode={isDarkMode} />
            <KpiCard icon={<TrendingUp className="w-5 h-5" />} label="Receita Fintech" value={fmt(overview.totalFintechRevenue)} color="green" isDarkMode={isDarkMode} />
            <KpiCard icon={<TrendingDown className="w-5 h-5" />} label="Custo Processador" value={fmt(overview.totalProcessorCost)} color="red" isDarkMode={isDarkMode} />
            <KpiCard icon={<DollarSign className="w-5 h-5" />} label="Posição Líquida" value={fmt(overview.netPosition)} color={netPositionValue !== null && netPositionValue >= 0 ? 'green' : 'red'} isDarkMode={isDarkMode} />
            <KpiCard icon={<CreditCard className="w-5 h-5" />} label="Receita Adesão" value={fmt(overview.totalMembershipRevenue)} color="indigo" isDarkMode={isDarkMode} />
          </div>
        ) : null}

        {/* Reconciliation Indicator */}
        {overview && !overview.escrowError && overview.netPosition !== null && (
          <div className={`${cardBg} rounded-xl p-4 mb-6 flex items-center gap-3 border ${border}`}>
            {netPositionValue !== null && netPositionValue >= 0 ? (
              <>
                <CheckCircle className="w-6 h-6 text-green-500" />
                <span className={`font-medium ${textPrimary}`}>Reconciliação OK — Saldo escrow cobre todas as contas virtuais</span>
              </>
            ) : (
              <>
                <AlertCircle className="w-6 h-6 text-red-500" />
                <span className="font-medium text-red-500">Atenção — Saldo escrow insuficiente para cobrir contas virtuais ({fmt(overview.netPosition)})</span>
              </>
            )}
          </div>
        )}

        {/* Membership Fee Breakdown */}
        {overview && (
          <div className={`${cardBg} rounded-xl p-5 mb-6 border ${border}`}>
            <h3 className={`text-lg font-semibold mb-3 ${textPrimary}`}>Taxa de Adesão (Mês Atual)</h3>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <p className={`text-sm ${textSecondary}`}>Total Arrecadado</p>
                <p className={`text-lg font-bold text-green-500`}>{fmt(overview.membershipFeeBreakdown.totalCharged)}</p>
              </div>
              <div>
                <p className={`text-sm ${textSecondary}`}>Pendente</p>
                <p className={`text-lg font-bold text-yellow-500`}>{fmt(overview.membershipFeeBreakdown.totalPending)}</p>
              </div>
              <div>
                <p className={`text-sm ${textSecondary}`}>Contas Cobradas</p>
                <p className={`text-lg font-bold ${textPrimary}`}>{overview.chargedAccountsCount}</p>
              </div>
              <div>
                <p className={`text-sm ${textSecondary}`}>Contas Pendentes</p>
                <p className={`text-lg font-bold text-yellow-500`}>{overview.pendingAccountsCount}</p>
              </div>
              <div>
                <p className={`text-sm ${textSecondary}`}>Contas Isentas</p>
                <p className={`text-lg font-bold text-blue-500`}>{overview.exemptAccountsCount}</p>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className={`flex gap-1 mb-4 p-1 rounded-lg ${isDarkMode ? 'bg-gray-800' : 'bg-gray-200'}`}>
          <button onClick={() => setActiveTab('transactions')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === 'transactions' ? (isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900 shadow') : textSecondary}`}>
            Transações
          </button>
          <button onClick={() => setActiveTab('accounts')}
            className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${activeTab === 'accounts' ? (isDarkMode ? 'bg-gray-700 text-white' : 'bg-white text-gray-900 shadow') : textSecondary}`}>
            Correntistas
          </button>
        </div>

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className={`${cardBg} rounded-xl border ${border} overflow-hidden`}>
            <div className="p-4 flex items-center gap-3 flex-wrap">
              <select value={txFilters.type || ''} onChange={e => setTxFilters(f => ({ ...f, type: e.target.value || undefined, page: 1 }))}
                className={`px-3 py-2 rounded-lg border text-sm ${inputBg}`}>
                <option value="">Todos os tipos</option>
                <option value="PIX_RECEIVE">PIX Recebido</option>
                <option value="PIX_SEND">PIX Enviado</option>
                <option value="WITHDRAWAL">Saque</option>
                <option value="REFUND">Estorno</option>
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Data</th>
                    <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Tipo</th>
                    <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Correntista</th>
                    <th className={`px-4 py-3 text-right font-medium ${textSecondary}`}>Bruto</th>
                    <th className={`px-4 py-3 text-right font-medium ${textSecondary}`}>Taxa Fintech</th>
                    <th className={`px-4 py-3 text-right font-medium ${textSecondary}`}>Taxa Processador</th>
                    <th className={`px-4 py-3 text-right font-medium ${textSecondary}`}>Líquido</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingTx ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center"><span className={textSecondary}>Carregando...</span></td></tr>
                  ) : txData?.items.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-8 text-center"><span className={textSecondary}>Nenhuma transação encontrada</span></td></tr>
                  ) : txData?.items.map(tx => (
                    <tr key={tx.id} className={`border-t ${border} hover:${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                      <td className={`px-4 py-3 ${textPrimary}`}>{new Date(tx.createdAt).toLocaleDateString('pt-BR')}</td>
                      <td className={`px-4 py-3`}><TypeBadge type={tx.type} /></td>
                      <td className={`px-4 py-3 ${textPrimary}`}>{tx.accountHolderName}</td>
                      <td className={`px-4 py-3 text-right ${textPrimary}`}>{fmt(tx.grossAmount)}</td>
                      <td className="px-4 py-3 text-right text-green-500">{fmt(tx.fintechFee)}</td>
                      <td className="px-4 py-3 text-right text-red-400">{fmt(tx.processorFee)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${textPrimary}`}>{fmt(tx.netAmount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {txData && txData.totalPages > 1 && (
              <Pagination page={txData.page} totalPages={txData.totalPages} onPageChange={p => setTxFilters(f => ({ ...f, page: p }))} isDarkMode={isDarkMode} />
            )}
          </div>
        )}

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <div className={`${cardBg} rounded-xl border ${border} overflow-hidden`}>
            <div className="p-4 flex items-center gap-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px]">
                <Search className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${textSecondary}`} />
                <input type="text" placeholder="Buscar por nome ou CPF/CNPJ..." value={accSearch}
                  onChange={e => { setAccSearch(e.target.value); setAccFilters(f => ({ ...f, page: 1 })); }}
                  className={`w-full pl-10 pr-3 py-2 rounded-lg border text-sm ${inputBg}`} />
              </div>
              <select value={accFilters.accountType || ''} onChange={e => setAccFilters(f => ({ ...f, accountType: e.target.value || undefined, page: 1 }))}
                className={`px-3 py-2 rounded-lg border text-sm ${inputBg}`}>
                <option value="">Todos os tipos</option>
                <option value="CUSTOMER">Cliente</option>
                <option value="EMPLOYEE">Funcionário</option>
                <option value="SUPPLIER">Fornecedor</option>
              </select>
              <select value={accFilters.feeStatus || ''} onChange={e => setAccFilters(f => ({ ...f, feeStatus: e.target.value || undefined, page: 1 }))}
                className={`px-3 py-2 rounded-lg border text-sm ${inputBg}`}>
                <option value="">Status taxa</option>
                <option value="PAID">Pago</option>
                <option value="PENDING">Pendente</option>
                <option value="EXEMPT">Isento</option>
              </select>
              <select value={accFilters.balanceStatus || ''} onChange={e => setAccFilters(f => ({ ...f, balanceStatus: e.target.value || undefined, page: 1 }))}
                className={`px-3 py-2 rounded-lg border text-sm ${inputBg}`}>
                <option value="">Saldo</option>
                <option value="with_balance">Com saldo</option>
                <option value="zero">Saldo zerado</option>
              </select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                    <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Correntista</th>
                    <th className={`px-4 py-3 text-left font-medium ${textSecondary}`}>Tipo</th>
                    <th className={`px-4 py-3 text-right font-medium ${textSecondary}`}>
                      <button onClick={() => setAccFilters(f => ({ ...f, sortBy: 'totalReceived', sortOrder: f.sortBy === 'totalReceived' && f.sortOrder === 'desc' ? 'asc' : 'desc' }))}
                        className="inline-flex items-center gap-1 hover:text-blue-500">
                        Total Recebido <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className={`px-4 py-3 text-right font-medium ${textSecondary}`}>
                      <button onClick={() => setAccFilters(f => ({ ...f, sortBy: 'totalBalance', sortOrder: f.sortBy === 'totalBalance' && f.sortOrder === 'desc' ? 'asc' : 'desc' }))}
                        className="inline-flex items-center gap-1 hover:text-blue-500">
                        Saldo <ArrowUpDown className="w-3 h-3" />
                      </button>
                    </th>
                    <th className={`px-4 py-3 text-right font-medium ${textSecondary}`}>Taxas Pagas</th>
                    <th className={`px-4 py-3 text-center font-medium ${textSecondary}`}>Adesão</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingAcc ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center"><span className={textSecondary}>Carregando...</span></td></tr>
                  ) : accData?.items.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-8 text-center"><span className={textSecondary}>Nenhuma conta encontrada</span></td></tr>
                  ) : accData?.items.map(acc => (
                    <tr key={acc.id} className={`border-t ${border} hover:${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}>
                      <td className={`px-4 py-3 ${textPrimary}`}>
                        <div>{acc.holderName}</div>
                        <div className={`text-xs ${textSecondary}`}>{acc.holderDocument}</div>
                      </td>
                      <td className={`px-4 py-3`}><AccountTypeBadge type={acc.accountType} /></td>
                      <td className={`px-4 py-3 text-right ${textPrimary}`}>{fmt(acc.totalReceived)}</td>
                      <td className={`px-4 py-3 text-right ${textPrimary}`}>
                        {fmt(acc.totalBalance)}
                        {acc.isZeroBalance && acc.hasActivity && (
                          <span className="ml-1 px-1.5 py-0.5 text-xs rounded bg-yellow-100 text-yellow-800">Zerado</span>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-right ${textSecondary}`}>{fmt(String(BigInt(acc.totalCashInFeesPaid || '0') + BigInt(acc.totalMembershipFeesPaid || '0')))}</td>
                      <td className="px-4 py-3 text-center"><FeeStatusBadge status={acc.membershipFeeStatus} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {accData && accData.totalPages > 1 && (
              <Pagination page={accData.page} totalPages={accData.totalPages} onPageChange={p => setAccFilters(f => ({ ...f, page: p }))} isDarkMode={isDarkMode} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ---- Helper Components ----

function KpiCard({ icon, label, value, color, isDarkMode, error }: {
  icon: React.ReactNode; label: string; value: string; color: string; isDarkMode: boolean; error?: boolean;
}) {
  const colorMap: Record<string, string> = {
    blue: 'text-blue-500 bg-blue-500/10',
    green: 'text-green-500 bg-green-500/10',
    red: 'text-red-500 bg-red-500/10',
    purple: 'text-purple-500 bg-purple-500/10',
    indigo: 'text-indigo-500 bg-indigo-500/10',
  };
  const c = colorMap[color] || colorMap.blue;
  const cardBg = isDarkMode ? 'bg-gray-800' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <div className={`${cardBg} rounded-xl p-4 border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded-lg ${c}`}>{icon}</div>
        <span className={`text-xs font-medium ${textSecondary}`}>{label}</span>
      </div>
      <p className={`text-lg font-bold ${error ? 'text-yellow-500' : textPrimary}`}>{value}</p>
    </div>
  );
}

function TypeBadge({ type }: { type: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    PIX_RECEIVE: { label: 'PIX Recebido', cls: 'bg-green-100 text-green-800' },
    PIX_SEND: { label: 'PIX Enviado', cls: 'bg-blue-100 text-blue-800' },
    WITHDRAWAL: { label: 'Saque', cls: 'bg-orange-100 text-orange-800' },
    REFUND: { label: 'Estorno', cls: 'bg-red-100 text-red-800' },
    FEE_CHARGE: { label: 'Taxa', cls: 'bg-purple-100 text-purple-800' },
  };
  const m = map[type] || { label: type, cls: 'bg-gray-100 text-gray-800' };
  return <span className={`px-2 py-1 text-xs font-medium rounded-full ${m.cls}`}>{m.label}</span>;
}

function AccountTypeBadge({ type }: { type: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    CUSTOMER: { label: 'Cliente', cls: 'bg-blue-100 text-blue-800' },
    EMPLOYEE: { label: 'Funcionário', cls: 'bg-indigo-100 text-indigo-800' },
    SUPPLIER: { label: 'Fornecedor', cls: 'bg-teal-100 text-teal-800' },
  };
  const m = map[type] || { label: type, cls: 'bg-gray-100 text-gray-800' };
  return <span className={`px-2 py-1 text-xs font-medium rounded-full ${m.cls}`}>{m.label}</span>;
}

function FeeStatusBadge({ status }: { status: string }) {
  const map: Record<string, { label: string; cls: string }> = {
    PAID: { label: 'Pago', cls: 'bg-green-100 text-green-800' },
    PENDING: { label: 'Pendente', cls: 'bg-yellow-100 text-yellow-800' },
    EXEMPT: { label: 'Isento', cls: 'bg-blue-100 text-blue-800' },
  };
  const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-800' };
  return <span className={`px-2 py-1 text-xs font-medium rounded-full ${m.cls}`}>{m.label}</span>;
}

function Pagination({ page, totalPages, onPageChange, isDarkMode }: {
  page: number; totalPages: number; onPageChange: (p: number) => void; isDarkMode: boolean;
}) {
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const btnCls = isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100';
  return (
    <div className={`flex items-center justify-between px-4 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
      <span className={`text-sm ${textSecondary}`}>Página {page} de {totalPages}</span>
      <div className="flex gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page <= 1}
          className={`p-2 rounded-lg ${btnCls} disabled:opacity-30`}><ChevronLeft className="w-4 h-4" /></button>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}
          className={`p-2 rounded-lg ${btnCls} disabled:opacity-30`}><ChevronRight className="w-4 h-4" /></button>
      </div>
    </div>
  );
}
